
import Page from '@/Page.vue';
import { Component, Prop, PropSync, Ref } from 'vue-property-decorator';
import { findingsStore } from '@/libs/findings/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import {
    CategoryModel,
    FindingModel,
    ImplementationStateEnum,
    MeasureModel,
    MeasureTypeEnum,
    PermissionTypeEnum,
    PlanItemModel,
    PlanItemStateEnum,
    ProcessTypeEnum,
    QueryOperator,
    ResponseModel,
    TenantUserModel,
} from '@/libs/Api';
import WorkTaskFindingItemMeasure from '../components/WorkTaskFindingItemMeasure.vue';
import { cloneDeep } from 'lodash';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import WorkTaskDialogFiles from '../components/WorkTaskDialogFiles.vue';
import moment from 'moment';
import WorkTaskEndShutdown from '@/libs/work-tasks/components/WorkTaskEndShutdown.vue';
import { measuresStore } from '@/libs/measures/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

@Component({
    components: {
        WorkTaskEndShutdown,
        WorkTaskFindingItemMeasure,
        WorkTaskDialogFiles,
    },
})
export default class WorkTaskFindingItem extends Page {
    /* Component props */
    @PropSync('finding') findingItem!: FindingModel;
    @Prop() planItem!: PlanItemModel;
    @Prop() category!: CategoryModel;

    /* Component state variables */
    dialog = false;
    valid = true;
    saving = false;
    hasChanges = true;
    dialogValid = true;
    endShutdownDialog = false;
    isEndShutdownMandatory = false;
    dialogModel: FindingModel | null = null;

    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;

    /* Auth variables */
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    /* Ref */
    @Ref() dialogForm!: any;
    @Ref() measureItems!: WorkTaskFindingItemMeasure[];

    get item(): FindingModel {
        return { ...this.findingItem };
    }

    get response(): ResponseModel {
        return this.planItem.responses!.first((x) => x.id == this.item.responseId);
    }

    // ✅ NOVÝ SYSTÉM: Kontroluje readonly podle kategorie-specifických oprávnění
    get isReadonly() {
        // Kontrola základních oprávnění v kontextu kategorie
        let canEditFinding = this.hasAccessToCategory(
            this.category.id,
            [PermissionTypeEnum.Findings, PermissionTypeEnum.FindingManager]
        );
        
        // Pro Audit processType musí být uživatel v correctionManagers nebo findingManagers
        if (canEditFinding && this.category.processType == ProcessTypeEnum.Audit) {
            canEditFinding = this.category.correctionManagers?.any((x) => x.tenantUserId! == this.tenantUser!.id!) ?? false;
            if (!canEditFinding) {
                canEditFinding = this.item.findingManagers?.any((x) => x.tenantUserId == this.tenantUser!.id!) ?? false;
            }
        }
        
        // Pokud má nález definované vlastní správce, musí být v seznamu
        if (canEditFinding) {
            if (this.item.useDefinedManagers == true) {
                if (!this.item.findingManagers?.any((x) => x.tenantUserId == this.tenantUser!.id!)) {
                    canEditFinding = false;
                }
            }
        }
        
        return (
            !canEditFinding ||
            this.item.measures!.any(
                (x) =>
                    x.implementationState != ImplementationStateEnum.New && this.category.processType == ProcessTypeEnum.Audit
            ) ||
            this.planItem.planItemState == PlanItemStateEnum.ClosedWithoutFindings ||
            this.planItem.planItemState == PlanItemStateEnum.ClosedWithIssueEscalation ||
            this.planItem.planItemState == PlanItemStateEnum.Closed
        );
    }

    get isAnyMeasureCompleted() {
        return this.item.measures!.any(
            (x) =>
                x.implementationState == ImplementationStateEnum.WaitingForVerification ||
                x.implementationState == ImplementationStateEnum.Verified
        );
    }

    openDialog() {
        this.dialogModel = cloneDeep(this.item);
        if (this.dialogModel.measures!.length == 0) {
            this.addMeasure();
        }
        this.dialogValid = true;
        this.dialog = true;
    }

    closeDialog() {
        this.dialog = false;
    }

    save() {
        const isValidMesages = this.measureItems.all((x: WorkTaskFindingItemMeasure) => x.validate());
        if (this.dialogForm.validate() && this.dialogModel) {
            this.saving = true;
            const dialogModel = cloneDeep(this.dialogModel)!;
            dialogModel.measures = this.measureItems?.map((e: WorkTaskFindingItemMeasure) => e.getMeasure()!) ?? [];
            dialogModel!.measures!.forEach((x) => {
                x.sequenceIndex = dialogModel!.measures!.indexOf(x) + 1;
            });
            if (this.category.processType != ProcessTypeEnum.IssueSolving) {
                dialogModel?.measures?.forEach((e) => {
                    if (e.implementerUserId) {
                        if (
                            e.measureImplementationUsers!.length == 0 ||
                            e.measureImplementationUsers![0].tenantUserId != e.implementerUserId
                        ) {
                            e.measureImplementationUsers = [
                                {
                                    tenantUserId: e.implementerUserId,
                                },
                            ];
                        }
                    }
                    if (
                        (e.implementationState == ImplementationStateEnum.WaitingForVerification ||
                            e.implementationState == ImplementationStateEnum.Verified) &&
                        !e.implementationTime
                    ) {
                        e.implementationTime = moment().format('YYYY-MM-DDTHH:mm:ss');
                    }
                    if (
                        (e.implementationState == ImplementationStateEnum.WaitingForVerification ||
                            e.implementationState == ImplementationStateEnum.Verified) &&
                        !e.implementUntil
                    ) {
                        e.implementUntil = e.implementationTime;
                    }
                });
            }
            if (dialogModel.isClosed) {
                dialogModel.closedById = this.tenantUser.id;
                dialogModel.closedAt = moment().format('YYYY-MM-DDTHH:mm:ss');
                dialogModel.measures = [];
            } else {
                dialogModel.closedById = null;
                dialogModel.closedAt = null;
            }
            findingsStore.dispatch(CrudAction.Update, { item: dialogModel });
            this.subscribe(findingsStore, CrudReponse.Update)
                .then(() => {
                    if (this.planItem.isWorkplaceShutdown && this.planItem.shutdownToDate == null) {
                        measuresStore.dispatch(
                            CrudAction.GetAll,
                            createCrudQueryPayload<MeasureModel>(
                                undefined,
                                [
                                    {
                                        field: 'finding.response.planItemId',
                                        op: QueryOperator.Eq,
                                        comparand: this.planItem.id!,
                                    },
                                    {
                                        field: 'implementationState',
                                        op: QueryOperator.In,
                                        comparand: [ImplementationStateEnum.New, ImplementationStateEnum.InProgress],
                                    },
                                ],
                                undefined,
                                false,
                                'NotImplementedMeasures'
                            )
                        );
                        this.subscribe(measuresStore, CrudReponse.GetAll, 'NotImplementedMeasures').then(
                            (e: MeasureModel[]) => {
                                this.isEndShutdownMandatory = e.length == 0;
                                this.endShutdownDialog = true;
                                this.saving = false;
                                this.$emit('planItemChanged');
                                this.closeDialog();
                            }
                        );
                    } else {
                        this.saving = false;
                        this.$emit('planItemChanged');
                        this.closeDialog();
                    }
                })
                .catch((e) => {
                    this.saving = false;
                    console.log(e);
                });
        }
    }

    addMeasure() {
        const measure = {
            implementationState: ImplementationStateEnum.New,
            measureType: this.category.useMeasureType ? MeasureTypeEnum.Corrective : null,
            implementationDescription: null,
            measureFiles: [],
            measureImplementationUsers: [],
        } as MeasureModel;
        if (
            this.category.processType == ProcessTypeEnum.Maintenance ||
            this.category.processType == ProcessTypeEnum.Checklist ||
            this.category.processType == ProcessTypeEnum.ProductInspection ||
            this.category.processType == ProcessTypeEnum.IssueSolving
        ) {
            measure.implementUntil = moment().format('YYYY-MM-DDT23:59:59');
        }
        this.dialogModel?.measures?.push(measure);
    }

    protected getResponseValueLabel(responseValue: number): string | null {
        return this?.category?.responseValues?.find((x) => x.value == responseValue)?.label ?? null;
    }

    public plainToHtml(value: string | null | undefined) {
        return value?.replaceAll('\n', '<br />');
    }
}
